import React from 'react';
import Plot from 'react-plotly.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowDown, ArrowUp, BarChart3, Users } from 'lucide-react';
import ChartExportButton from "@/components/shared/ChartExportButton";
import { MetricTile } from '@/components/shared/MetricTile';
import { useIsMobile } from '@/hooks/use-mobile';
import { plotlyChartChrome, usePlotlyTheme } from '@/lib/plotlyTheme';

interface PercentileData {
  year: number;
  occ_code: string;
  occ_title: string;
  a_p10?: number;
  a_p25?: number;
  a_median?: number;
  a_p75?: number;
  a_p90?: number;
  employment: number;
}

interface PercentileBreakdownProps {
  data: PercentileData[];
  title?: string;
  /** @deprecated Percentiles are now free for everyone; retained for call-site compatibility. */
  isPro?: boolean;
  rppAdjusted?: boolean;
}

export const PercentileBreakdown: React.FC<PercentileBreakdownProps> = ({
  data,
  title = "Salary Distribution Analysis",
  rppAdjusted = false
}) => {
  const isMobile = useIsMobile();
  const { colors, themeKey } = usePlotlyTheme();

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-muted-foreground py-8">
            No percentile data available for this selection
          </div>
        </CardContent>
      </Card>
    );
  }

  const latestData = data[data.length - 1];
  const years = data.map(d => d.year);

  // Prepare traces for percentile bands
  const traces = [
    // P10-P25 band
    {
      x: [...years, ...years.slice().reverse()],
      y: [
        ...data.map(d => d.a_p25 || 0),
        ...data.slice().reverse().map(d => d.a_p10 || 0)
      ],
      fill: 'toself',
      fillcolor: 'rgba(99, 102, 241, 0.1)',
      line: { color: 'transparent' },
      showlegend: false,
      hoverinfo: 'skip',
      name: 'P10-P25 Range'
    },
    // P25-P75 band
    {
      x: [...years, ...years.slice().reverse()],
      y: [
        ...data.map(d => d.a_p75 || 0),
        ...data.slice().reverse().map(d => d.a_p25 || 0)
      ],
      fill: 'toself',
      fillcolor: 'rgba(99, 102, 241, 0.2)',
      line: { color: 'transparent' },
      showlegend: false,
      hoverinfo: 'skip',
      name: 'P25-P75 Range'
    },
    // P75-P90 band
    {
      x: [...years, ...years.slice().reverse()],
      y: [
        ...data.map(d => d.a_p90 || 0),
        ...data.slice().reverse().map(d => d.a_p75 || 0)
      ],
      fill: 'toself',
      fillcolor: 'rgba(99, 102, 241, 0.1)',
      line: { color: 'transparent' },
      showlegend: false,
      hoverinfo: 'skip',
      name: 'P75-P90 Range'
    },
    // Median line
    {
      x: years,
      y: data.map(d => d.a_median || 0),
      type: 'scatter',
      mode: 'lines+markers',
      name: 'Median (P50)',
      line: { color: '#6366f1', width: 3 },
      marker: { color: '#6366f1', size: 6 }
    },
    // P10 line
    {
      x: years,
      y: data.map(d => d.a_p10 || 0),
      type: 'scatter',
      mode: 'lines',
      name: '10th Percentile',
      line: { color: '#ef4444', width: 2, dash: 'dot' }
    },
    // P90 line
    {
      x: years,
      y: data.map(d => d.a_p90 || 0),
      type: 'scatter',
      mode: 'lines',
      name: '90th Percentile',
      line: { color: '#22c55e', width: 2, dash: 'dot' }
    }
  ];

  const chrome = plotlyChartChrome(colors, { isMobile });

  const layout = {
    title: {
      text: `${latestData.occ_title} - Salary Distribution`,
      font: chrome.titleFont
    },
    xaxis: {
      title: chrome.axisTitle('Year'),
      type: 'linear',
      dtick: 2,
      ...chrome.axisDefaults
    },
    yaxis: {
      title: chrome.axisTitle(`Annual Salary${rppAdjusted ? ' (Real)' : ''} ($)`),
      tickformat: '$,.0f',
      ...chrome.axisDefaults
    },
    hovermode: 'x unified',
    hoverlabel: chrome.hoverlabel,
    showlegend: true,
    legend: chrome.legend('overlay'),
    margin: { l: 80, r: 28, t: 60, b: 60 },
    paper_bgcolor: chrome.paper_bgcolor,
    plot_bgcolor: chrome.plot_bgcolor,
    font: chrome.font
  };

  const config = {
    displayModeBar: !isMobile,
    modeBarButtonsToAdd: ['pan2d', 'autoScale2d'],
    modeBarButtonsToRemove: ['lasso2d', 'select2d'],
    displaylogo: false,
    responsive: true
  };

  // Calculate distribution stats for current year
  const currentStats = {
    p10: latestData.a_p10 || 0,
    p25: latestData.a_p25 || 0,
    median: latestData.a_median || 0,
    p75: latestData.a_p75 || 0,
    p90: latestData.a_p90 || 0,
    iqr: (latestData.a_p75 || 0) - (latestData.a_p25 || 0),
    range: (latestData.a_p90 || 0) - (latestData.a_p10 || 0)
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex w-full min-w-0 items-start justify-between gap-3">
          <div className="min-w-0 flex-1 space-y-1">
            <CardTitle className="text-lg leading-snug break-words">{title}</CardTitle>
            {rppAdjusted && <Badge variant="outline">Inflation Adjusted</Badge>}
          </div>
          <ChartExportButton
            chartId="percentile-breakdown-chart"
            filename={`percentile-breakdown-${latestData.occ_title?.replace(/[^a-z0-9]/gi, '_') || 'chart'}`}
          />
        </div>
        <CardDescription>
          Salary distribution across percentiles with {data.length} years of data
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Distribution Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MetricTile
            tone="red"
            icon={ArrowDown}
            badge="Low"
            value={`$${currentStats.p10.toLocaleString()}`}
            label="P10"
          />
          <MetricTile
            tone="orange"
            icon={ArrowDown}
            badge="Lower mid"
            value={`$${currentStats.p25.toLocaleString()}`}
            label="P25"
          />
          <MetricTile
            tone="blue"
            icon={ArrowUp}
            badge="Upper mid"
            value={`$${currentStats.p75.toLocaleString()}`}
            label="P75"
          />
          <MetricTile
            tone="green"
            icon={ArrowUp}
            badge="High"
            value={`$${currentStats.p90.toLocaleString()}`}
            label="P90"
          />
        </div>

        {/* Interactive Chart */}
        <div id="percentile-breakdown-chart" className="h-96 min-w-0 w-full overflow-x-auto">
          <Plot
            data={traces}
            layout={layout}
            config={config}
            style={{ width: '100%', height: '100%' }}
            useResizeHandler={true}
            key={themeKey}
          />
        </div>

        {/* Analysis Insights */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <MetricTile
            tone="blue"
            icon={BarChart3}
            badge="Spread"
            value={`$${currentStats.iqr.toLocaleString()}`}
            label={`IQR · Range $${currentStats.range.toLocaleString()}`}
          />
          <MetricTile
            tone="purple"
            icon={Users}
            badge={String(latestData.year)}
            value={latestData.employment.toLocaleString()}
            label="Latest Employment"
          />
        </div>
      </CardContent>
    </Card>
  );
};